import React, { useState, useRef, useEffect } from 'react';
import resourceService, { CreateResourceData } from '../../services/resourceService';
import { quizService, QuizProfile } from '../../services/quizService';

interface Segment {
  id: number;
  name: string;
  description: string;
  active_leads: number;
}

interface ResourceUploaderProps {
  segments: Segment[];
  onSuccess: (resource: any) => void;
  onCancel: () => void;
}

const ResourceUploader: React.FC<ResourceUploaderProps> = ({ segments, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'general',
    priority: 5,
    require_email: true,
    segment_ids: [] as number[],
    quiz_profile_ids: [] as number[]
  });
  const [quizProfiles, setQuizProfiles] = useState<QuizProfile[]>([]);
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const categories = [
    { value: 'autism', label: 'Autism Support' },
    { value: 'adhd', label: 'ADHD Management' },
    { value: 'learning-differences', label: 'Learning Differences' },
    { value: 'speech-language', label: 'Speech & Language' },
    { value: 'behavioral-support', label: 'Behavioral Support' },
    { value: 'general', label: 'General Resources' }
  ];

  const allowedFileTypes = [
    '.pdf', '.docx', '.doc', '.txt', '.mp4', '.webm', '.jpg', '.jpeg', '.png'
  ];

  // Fetch quiz profiles on mount
  useEffect(() => {
    const fetchQuizProfiles = async () => {
      try {
        const profiles = await quizService.getProfiles();
        setQuizProfiles(profiles);
      } catch (error) {
        console.error('Error fetching quiz profiles:', error);
      }
    };

    fetchQuizProfiles();
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: parseInt(value) || 0 }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSegmentChange = (segmentId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      segment_ids: checked
        ? [...prev.segment_ids, segmentId]
        : prev.segment_ids.filter(id => id !== segmentId)
    }));
  };

  const handleQuizProfileChange = (profileId: number, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      quiz_profile_ids: checked
        ? [...prev.quiz_profile_ids, profileId]
        : prev.quiz_profile_ids.filter(id => id !== profileId)
    }));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      handleFiles(selectedFiles);
    }
  };

  const handleFiles = (newFiles: File[]) => {
    // Validate file types
    const validFiles = newFiles.filter(file => {
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      return allowedFileTypes.includes(extension);
    });

    if (validFiles.length !== newFiles.length) {
      setError(`Some files were skipped. Only these types are allowed: ${allowedFileTypes.join(', ')}`);
    } else {
      setError(null);
    }

    // Add to existing files
    setFiles(prev => [...prev, ...validFiles]);
  };

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (fileName: string): string => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'pdf': return '📄';
      case 'docx':
      case 'doc': return '📝';
      case 'mp4':
      case 'webm': return '🎥';
      case 'jpg':
      case 'jpeg':
      case 'png': return '🖼️';
      default: return '📎';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Title is required');
      return;
    }
    
    if (!formData.description.trim()) {
      setError('Description is required');
      return;
    }
    
    if (files.length === 0) {
      setError('At least one file is required');
      return;
    }

    if (formData.segment_ids.length === 0 && formData.quiz_profile_ids.length === 0) {
      setError('Please select at least one segment or quiz profile');
      return;
    }

    try {
      setUploading(true);
      setError(null);

      const createData: CreateResourceData = {
        ...formData,
        files,
        metadata: {
          created_by: 'admin',
          file_count: files.length
        }
      };

      console.log('📤 Submitting resource creation:', {
        title: createData.title,
        category: createData.category,
        fileCount: createData.files.length,
        segmentCount: createData.segment_ids.length,
        quizProfileCount: createData.quiz_profile_ids.length
      });

      const result = await resourceService.createResource(createData);
      console.log('✅ Resource created successfully:', result);

      // If quiz profiles were selected, assign them
      if (formData.quiz_profile_ids.length > 0) {
        await resourceService.assignToQuizProfiles(result.resource_id, formData.quiz_profile_ids, false);
        console.log('✅ Resource assigned to quiz profiles');
      }

      onSuccess(result);
    } catch (err) {
      console.error('❌ Error creating resource:', err);
      setError(err instanceof Error ? err.message : 'Failed to create resource');
    } finally {
      setUploading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Basic Information */}
      <div className="grid grid-cols-1 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Resource Title *
          </label>
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleInputChange}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="e.g., Complete Guide to Autism Support"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description *
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            placeholder="Describe what this resource contains and how it helps families..."
            required
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              {categories.map(category => (
                <option key={category.value} value={category.value}>
                  {category.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Priority (1-10)
            </label>
            <input
              type="number"
              name="priority"
              value={formData.priority}
              onChange={handleInputChange}
              min="1"
              max="10"
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>

          <div className="flex items-center">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="require_email"
                checked={formData.require_email}
                onChange={handleInputChange}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">Require email to download</span>
            </label>
          </div>
        </div>
      </div>

      {/* File Upload */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Files * (PDF, DOCX, Images, Videos)
        </label>
        
        <div
          className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            dragActive 
              ? 'border-indigo-500 bg-indigo-50' 
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        >
          <div className="text-4xl mb-2">📁</div>
          <p className="text-gray-600 mb-2">
            Drag and drop files here, or{' '}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="text-indigo-600 hover:text-indigo-700 underline"
            >
              browse files
            </button>
          </p>
          <p className="text-sm text-gray-500">
            Supported: {allowedFileTypes.join(', ')} (max 50MB each)
          </p>
          
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={allowedFileTypes.join(',')}
            onChange={handleFileInput}
            className="hidden"
          />
        </div>

        {/* File List */}
        {files.length > 0 && (
          <div className="mt-4 space-y-2">
            <h4 className="text-sm font-medium text-gray-700">Selected Files:</h4>
            {files.map((file, index) => (
              <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-md">
                <div className="flex items-center space-x-3">
                  <span className="text-xl">{getFileIcon(file.name)}</span>
                  <div>
                    <p className="text-sm font-medium text-gray-900">{file.name}</p>
                    <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Segment Assignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assign to Segments (Select all that apply)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto border border-gray-200 rounded-md p-4">
          {segments.map(segment => (
            <label key={segment.id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.segment_ids.includes(segment.id)}
                onChange={(e) => handleSegmentChange(segment.id, e.target.checked)}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">{segment.name}</span>
                <p className="text-xs text-gray-500">{segment.active_leads} active leads</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Quiz Profile Assignment */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Assign to Quiz Profiles (Select all that apply)
        </label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-64 overflow-y-auto border border-purple-200 rounded-md p-4 bg-purple-50">
          {quizProfiles.map(profile => (
            <label key={profile.id} className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.quiz_profile_ids.includes(profile.id)}
                onChange={(e) => handleQuizProfileChange(profile.id, e.target.checked)}
                className="rounded border-purple-300 text-purple-600 focus:ring-purple-500"
              />
              <div className="flex-1">
                <span className="text-sm font-medium text-gray-900">{profile.name}</span>
                <p className="text-xs text-purple-600">{profile.slug}</p>
              </div>
            </label>
          ))}
        </div>
        {quizProfiles.length === 0 && (
          <p className="text-sm text-gray-500 mt-2 italic">No quiz profiles available</p>
        )}
      </div>

      {formData.segment_ids.length === 0 && formData.quiz_profile_ids.length === 0 && (
        <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-md p-3">
          ⚠️ Please select at least one segment or quiz profile to assign this resource.
        </p>
      )}

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <div className="flex items-center gap-2">
            <span>⚠️</span>
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          disabled={uploading}
          className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={uploading || files.length === 0}
          className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
        >
          {uploading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              Uploading...
            </>
          ) : (
            <>
              <span>📤</span>
              Create Resource
            </>
          )}
        </button>
      </div>
    </form>
  );
};

export default ResourceUploader;